/* eslint-disable array-bracket-newline */
import { useEffect, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { ISmartTrade, ITicker, Network, enums } from '@kattana-io/common';
import useCollectTickers from '../useCollectTickers';
import useCollectIcons from '../useCollectTokens';
import { ETableTimeframe, parseTableTimeframe } from '../helpers';
import { KattanaAPI } from '../../../store/createStore';
import useTicker from '../../../hooks/useTicker';

interface IExtendedSmartTrade extends ISmartTrade {
  trades: ISmartTrade[];
}

export interface ISwap {
  chain: string;
  srcTokenAddress: string;
  srcTokenName: string;
  srcTokenAmount: number;
  srcTokenIcon?: string;
  destTokenAddress: string;
  destTokenName: string;
  destTokenAmount: number;
  destTokenIcon?: string;
  ticker?: ITicker;
  timestamp: number;
  price: number;
  priceUSD: number;
  valueUSD: number;
  wallet: string;
  tx: string;
  exchange: string;
  swapType: enums.SwapType;
  uuid: string;
  blocknumber: number;
  order: number;
  isBuy: boolean;
}

export interface IMyPosition extends ISwap {
  subTrades: ISwap[];
}

const getIsBuy = (
  buy: boolean,
  swap_type: enums.SwapType,
  flipped: boolean
) => {
  if (
    swap_type === enums.SwapType.PAIR_SWAP ||
    swap_type === enums.SwapType.PROXY_PAIR_SWAP
  ) {
    return flipped ? !buy : buy;
  }
  return buy;
};

const getAmounts = (
  buy: boolean,
  swap_type: enums.SwapType,
  amount0: number,
  amount1: number,
  flipped: boolean
) => {
  if (
    swap_type === enums.SwapType.PAIR_SWAP ||
    swap_type === enums.SwapType.PROXY_PAIR_SWAP
  ) {
    const srcAmount = buy ? amount1 : amount0;
    const destAmount = buy ? amount0 : amount1;
    return {
      srcAmount: flipped ? destAmount : srcAmount,
      destAmount: flipped ? srcAmount : destAmount,
    };
  }
  return { srcAmount: amount0, destAmount: amount1 };
};

const getPrices = (
  swap_type: enums.SwapType,
  pricea: string,
  priceb: string,
  priceaUSD: string,
  pricebUSD: string,
  flipped: boolean,
  isBase: boolean
) => {
  if (
    swap_type === enums.SwapType.PAIR_SWAP ||
    swap_type === enums.SwapType.PROXY_PAIR_SWAP
  ) {
    const srcPrice = Number(isBase ? pricea : priceb);
    const destPrice = Number(isBase ? priceb : pricea);
    const srcPriceUSD = Number(isBase ? priceaUSD : pricebUSD);
    const destPriceUSD = Number(isBase ? pricebUSD : priceaUSD);
    return {
      price: flipped ? destPrice : srcPrice,
      priceUSD: flipped ? destPriceUSD : srcPriceUSD,
    };
  }

  return {
    price: Number(pricea),
    priceUSD: Number(priceaUSD),
  };
};

type TickersMap = Record<string, ITicker | undefined>;
type TokensMap = Record<
  string,
  { name: string; icon: string; decimals: number } | undefined
>;

const parseSwaps = (
  swaps: ISmartTrade[],
  tickers: TickersMap,
  tokens: TokensMap
) => {
  const parsedSwaps: ISwap[] = [];
  swaps.forEach((item) => {
    const {
      chain,
      pair,
      amount0,
      amount1,
      pricea,
      priceb,
      pricea_usd,
      priceb_usd,
      buy,
      swap_type,
      date,
      value_usd,
      wallet,
      tx,
      blocknumber,
      order,
      src_token,
      dst_token,
    } = item;

    const key = `${chain}-${pair}`;
    // get ticker
    const ticker = tickers[key];

    if (ticker && pair) {
      const {
        base_address,
        quote_address,
        base_decimals,
        quote_decimals,
        base,
        quote,
      } = useTicker(ticker);
      const isBuy = getIsBuy(buy, swap_type, ticker.flipped);
      // check whether src token is base ticker token
      const srcTokenAddress = isBuy ? quote_address : base_address;
      const destTokenAddress = isBuy ? base_address : quote_address;
      // get decimals for dest and src tokens
      const srcTokenDecimals = isBuy ? quote_decimals : base_decimals;
      const destTokenDecimals = isBuy ? base_decimals : quote_decimals;
      // get amounts for dest and src tokens
      const amounts = getAmounts(
        isBuy,
        swap_type,
        amount0,
        amount1,
        ticker.flipped
      );
      const srcTokenAmount = new BigNumber(amounts.srcAmount).div(
        10 ** Number(srcTokenDecimals ?? 18)
      );
      const destTokenAmount = new BigNumber(amounts.destAmount).div(
        10 ** Number(destTokenDecimals ?? 18)
      );
      // get better token name
      const srcTokenName = isBuy ? quote : base;
      const destTokenName = isBuy ? base : quote;
      // get token icon
      const srcTokenIcon = tokens[`${chain}-${srcTokenAddress}`]?.icon;
      const destTokenIcon = tokens[`${chain}-${destTokenAddress}`]?.icon;
      // get trade date
      const timestamp = new Date(date).getTime() / 1000;
      // get token price
      const { price, priceUSD } = getPrices(
        swap_type,
        pricea,
        priceb,
        pricea_usd,
        priceb_usd,
        ticker.flipped,
        base_address === srcTokenAddress
      );

      parsedSwaps.push({
        chain,
        srcTokenAddress,
        srcTokenName,
        srcTokenAmount: srcTokenAmount.toNumber(),
        srcTokenIcon,
        destTokenAddress,
        destTokenName,
        destTokenAmount: destTokenAmount.toNumber(),
        destTokenIcon,
        ticker,
        timestamp,
        price,
        priceUSD,
        valueUSD: Number(value_usd),
        wallet,
        tx,
        exchange: ticker.exchange,
        swapType: swap_type,
        uuid: `${tx}-${order}`,
        blocknumber,
        order,
        isBuy,
      });
    } else {
      const isBuy = getIsBuy(buy, swap_type, false);
      // check src and dest tokens
      const srcTokenAddress = isBuy ? dst_token : src_token;
      const destTokenAddress = isBuy ? src_token : dst_token;
      // get tokens info
      const srcTokenInfo = tokens[`${chain}-${srcTokenAddress}`];
      const destTokenInfo = tokens[`${chain}-${destTokenAddress}`];
      // get decimals for dest and src tokens
      const srcTokenDecimals = srcTokenInfo?.decimals ?? 18;
      const destTokenDecimals = destTokenInfo?.decimals ?? 18;
      // get amounts for dest and src tokens
      const amounts = getAmounts(isBuy, swap_type, amount0, amount1, false);
      const srcTokenAmount = new BigNumber(amounts.srcAmount).div(
        10 ** Number(srcTokenDecimals)
      );
      const destTokenAmount = new BigNumber(amounts.destAmount).div(
        10 ** Number(destTokenDecimals)
      );
      // get better token name
      const srcTokenName = srcTokenInfo?.name || '';
      const destTokenName = destTokenInfo?.name || '';
      // get token icon
      const srcTokenIcon = tokens[`${chain}-${srcTokenAddress}`]?.icon;
      const destTokenIcon = tokens[`${chain}-${destTokenAddress}`]?.icon;
      // get trade date
      const timestamp = new Date(date).getTime() / 1000;
      // get token price
      const { price, priceUSD } = getPrices(
        swap_type,
        pricea,
        priceb,
        pricea_usd,
        priceb_usd,
        false,
        src_token === srcTokenAddress
      );

      parsedSwaps.push({
        chain,
        srcTokenAddress,
        srcTokenName,
        srcTokenAmount: srcTokenAmount.toNumber(),
        srcTokenIcon,
        destTokenAddress,
        destTokenName,
        destTokenAmount: destTokenAmount.toNumber(),
        destTokenIcon,
        ticker: undefined,
        timestamp,
        price,
        priceUSD,
        valueUSD: Number(value_usd),
        wallet,
        tx,
        exchange: '-',
        swapType: swap_type,
        uuid: `${tx}-${order}`,
        blocknumber,
        order,
        isBuy,
      });
    }
  });
  return parsedSwaps;
};

type UseGetMyPositionsArgs = {
  address?: string;
  chain?: number;
  timeframe: ETableTimeframe;
};

type UseGetMyPositionsData = {
  loading: boolean;
  data: IMyPosition[];
};

const useGetMyPositions = ({
  address,
  chain,
  timeframe,
}: UseGetMyPositionsArgs): UseGetMyPositionsData => {
  const [swaps, setSwaps] = useState<IExtendedSmartTrade[]>([]);
  const [loading, setLoading] = useState(true);

  const { tickers, loadingTickers } = useCollectTickers(swaps);
  const { tokens, loadingTokens } = useCollectIcons(swaps);

  useEffect(() => {
    const controller = new AbortController();
    if (address && chain) {
      setLoading(true);
      KattanaAPI.smartTrades(
        address,
        Network.fromChainId(chain).getSymbol(),
        parseTableTimeframe(timeframe),
        controller
      ).then((res) => {
        if (res) {
          const data = Object.keys(res).reduce<IExtendedSmartTrade[]>(
            (acc, key) => {
              const txTrades = res[key];
              // divide tx trades to main trade and sub trades
              const extendedTrade: IExtendedSmartTrade = {
                ...txTrades[0],
                trades: txTrades.slice(1),
              };

              return [...acc, extendedTrade];
            },
            []
          );
          setSwaps(data);
        } else {
          setSwaps([]);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }

    return () => controller.abort();
  }, [timeframe, address, chain]);

  const data = useMemo(() => {
    if (loading || loadingTickers || loadingTokens) {
      return { loading: true, data: [] };
    }
    const myPositions: IMyPosition[] = swaps.map((item) => {
      // get info about main trade
      const [mainTrade] = parseSwaps([item], tickers, tokens);
      // get info about sub trades
      const subTrades = parseSwaps(item.trades, tickers, tokens);
      return {
        ...mainTrade,
        subTrades,
      };
    });
    return { loading: false, data: myPositions };
  }, [swaps, loading, loadingTickers, loadingTokens]);

  return data;
};

export default useGetMyPositions;
